import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { apiService } from '../services/api';
import './DonorCircleTasks.css';

const money = (n) => (n == null ? '' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n));

const NOMINATION_STATUS_TEXT = {
    submitted: 'Received — the Funds4Charity team will review and invite them.',
    invited: 'Invited — we have sent them an invitation to join.',
    registered: 'Registered — they are completing their profile.',
    live: 'Live — their profile is in the nonprofit directory.',
    declined: 'Not able to participate this year.',
};

/**
 * DonorCircleTasks — the two Donor Circle onboarding tasks shown on the event Home page
 * for Donor organizations: nominate one nonprofit and confirm the commitment amount.
 * Renders task cards using the same styles as the other Home tasks.
 *
 * `stepStart` (optional) is the checklist number of the first card; when omitted the
 * pending indicator falls back to a plain circle.
 */
const DonorCircleTasks = ({ eventCompanyId, onStatusChange, stepStart }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loadError, setLoadError] = useState('');
    const [openPanel, setOpenPanel] = useState(null); // 'nomination' | 'commitment' | null
    const [saving, setSaving] = useState(false);
    const [nomination, setNomination] = useState({ nominee_name: '', nominee_website: '', nominee_contact_name: '', nominee_contact_email: '', nominee_reason: '' });
    const [commitment, setCommitment] = useState({ amount: '', notes: '' });

    const load = useCallback(async () => {
        if (!eventCompanyId) return;
        setLoadError('');
        try {
            const res = await apiService.getMyDonorCircle(eventCompanyId);
            setData(res.data);
            if (res.data.nomination) {
                const n = res.data.nomination;
                setNomination({
                    nominee_name: n.nominee_name || '', nominee_website: n.nominee_website || '', nominee_contact_name: n.nominee_contact_name || '',
                    nominee_contact_email: n.nominee_contact_email || '', nominee_reason: n.nominee_reason || '',
                });
            }
            setCommitment({
                amount: res.data.commitment?.amount ?? res.data.default_commitment ?? 250000,
                notes: res.data.commitment?.notes || '',
            });
            onStatusChange?.({ nominationDone: !!res.data.nomination, commitmentDone: !!res.data.commitment });
        } catch (e) {
            console.warn('Could not load Donor Circle info', e);
            setLoadError('We could not load your Donor Circle tasks.');
        }
    }, [eventCompanyId, onStatusChange]);

    useEffect(() => { load(); }, [load]);

    const saveNomination = async (e) => {
        e.preventDefault();
        setError('');
        if (!nomination.nominee_name.trim()) { setError('Please enter the nonprofit\u2019s name.'); return; }
        setSaving(true);
        try {
            await apiService.saveMyNomination(eventCompanyId, {
                nominee_name: nomination.nominee_name.trim(),
                nominee_website: nomination.nominee_website.trim() || null,
                nominee_contact_name: nomination.nominee_contact_name.trim() || null,
                nominee_contact_email: nomination.nominee_contact_email.trim() || null,
                nominee_reason: nomination.nominee_reason.trim() || null,
            });
            setOpenPanel(null);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not save your nomination.');
        } finally {
            setSaving(false);
        }
    };

    const saveCommitment = async (e) => {
        e.preventDefault();
        setError('');
        const amount = Number(commitment.amount);
        if (!Number.isFinite(amount) || amount <= 0) { setError('Please enter a commitment amount.'); return; }
        const minimum = data?.default_commitment ?? 250000;
        if (amount < minimum) { setError(`The Donor Circle commitment is ${money(minimum)} or more.`); return; }
        setSaving(true);
        try {
            await apiService.saveMyCommitment(eventCompanyId, { amount, notes: commitment.notes.trim() || null });
            setOpenPanel(null);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not save your commitment.');
        } finally {
            setSaving(false);
        }
    };

    const renderPending = (offset) => (
        typeof stepStart === 'number'
            ? <span className="task-circle task-step" aria-label={`Step ${stepStart + offset}`}>{stepStart + offset}</span>
            : <Circle size={20} className="task-circle" />
    );

    if (!eventCompanyId) return null;
    if (!data) {
        if (!loadError) return null;
        return (
            <div className="task-card">
                <div className="task-status">{renderPending(0)}</div>
                <div className="task-content">
                    <h3 className="task-title">Donor Circle</h3>
                    <p className="task-description" role="alert">{loadError}</p>
                </div>
                <div className="task-actions-row">
                    <button type="button" className="task-action-btn secondary" onClick={load}>Try again</button>
                </div>
            </div>
        );
    }

    const nom = data.nomination;
    const com = data.commitment;
    const nominationLocked = nom && nom.status !== 'submitted';
    const minimumCommitment = data.default_commitment ?? 250000;
    const nominationOpen = openPanel === 'nomination';
    const commitmentOpen = openPanel === 'commitment';

    return (
        <>
            {/* Nominate a nonprofit */}
            <div className={`task-card ${nom ? 'is-complete' : ''} ${nominationOpen ? 'is-open' : ''}`}>
                <div className="task-status">{nom ? <CheckCircle size={20} className="task-checkmark" aria-label="Done" /> : renderPending(0)}</div>
                <div className="task-content">
                    <div className="task-heading">
                        <h3 className="task-title">{nom ? 'Nonprofit nominated' : 'Nominate a nonprofit'}</h3>
                    </div>
                    <p className="task-description">
                        {nom
                            ? <>You nominated <strong>{nom.nominee_name}</strong> · {NOMINATION_STATUS_TEXT[nom.status] || nom.status}</>
                            : 'Each Donor Circle member nominates one nonprofit to participate. Tell us who and how to reach them.'}
                    </p>
                </div>
                <div className="task-actions-row">
                    {!nominationLocked && !nominationOpen && (
                        <button
                            type="button"
                            className={`task-action-btn ${nom ? 'ghost' : ''}`}
                            aria-expanded={nominationOpen}
                            onClick={() => { setError(''); setOpenPanel(nominationOpen ? null : 'nomination'); }}
                        >
                            {nom ? 'Edit' : 'Nominate a nonprofit'}
                        </button>
                    )}
                </div>
                {nominationOpen && (
                    <form className="donor-panel" onSubmit={saveNomination}>
                        <div className="donor-panel-grid">
                            <label className="donor-field donor-field-full">
                                <span className="donor-field-label">Nonprofit name <span className="donor-required">*</span></span>
                                <input value={nomination.nominee_name} onChange={(e) => setNomination({ ...nomination, nominee_name: e.target.value })} maxLength={255} required />
                            </label>
                            <label className="donor-field donor-field-full">
                                <span className="donor-field-label">Website</span>
                                <input value={nomination.nominee_website} onChange={(e) => setNomination({ ...nomination, nominee_website: e.target.value })} placeholder="https://" />
                            </label>
                            <label className="donor-field">
                                <span className="donor-field-label">Contact name</span>
                                <input value={nomination.nominee_contact_name} onChange={(e) => setNomination({ ...nomination, nominee_contact_name: e.target.value })} />
                            </label>
                            <label className="donor-field">
                                <span className="donor-field-label">Contact email</span>
                                <input type="email" value={nomination.nominee_contact_email} onChange={(e) => setNomination({ ...nomination, nominee_contact_email: e.target.value })} />
                            </label>
                            <label className="donor-field donor-field-full">
                                <span className="donor-field-label">Why this organization? <span className="donor-optional">(optional)</span></span>
                                <textarea rows={3} value={nomination.nominee_reason} onChange={(e) => setNomination({ ...nomination, nominee_reason: e.target.value })} maxLength={2000} />
                            </label>
                        </div>
                        <p className="donor-panel-hint">The Funds4Charity team reviews every nomination before inviting the nonprofit.</p>
                        {error && <div className="donor-panel-error" role="alert">{error}</div>}
                        <div className="donor-panel-actions">
                            <button type="button" className="task-action-btn ghost" onClick={() => setOpenPanel(null)}>Cancel</button>
                            <button type="submit" className="task-action-btn" disabled={saving}>{saving ? 'Saving…' : 'Save nomination'}</button>
                        </div>
                    </form>
                )}
            </div>

            {/* Commitment */}
            <div className={`task-card ${com ? 'is-complete' : ''} ${commitmentOpen ? 'is-open' : ''}`}>
                <div className="task-status">{com ? <CheckCircle size={20} className="task-checkmark" aria-label="Done" /> : renderPending(1)}</div>
                <div className="task-content">
                    <div className="task-heading">
                        <h3 className="task-title">{com ? 'Commitment confirmed' : 'Confirm your commitment'}</h3>
                    </div>
                    <p className="task-description">
                        {com
                            ? <>Commitment: <strong>{money(com.amount)}</strong> · {com.status}</>
                            : <>Donor Circle members commit {money(data.default_commitment)} to the nonprofits in the network. Confirm your amount, or enter more.</>}
                    </p>
                </div>
                <div className="task-actions-row">
                    {!commitmentOpen && (
                    <button
                        type="button"
                        className={`task-action-btn ${com ? 'secondary' : ''}`}
                        aria-expanded={commitmentOpen}
                        onClick={() => { setError(''); setOpenPanel(commitmentOpen ? null : 'commitment'); }}
                    >
                        {com ? 'Update commitment' : 'Confirm commitment'}
                    </button>
                    )}
                </div>
                {commitmentOpen && (
                    <form className="donor-panel" onSubmit={saveCommitment}>
                        <div className="donor-panel-grid">
                            <label className="donor-field">
                                <span className="donor-field-label">Commitment amount (USD) <span className="donor-required">*</span></span>
                                <input type="number" min={minimumCommitment} step={1000} value={commitment.amount} onChange={(e) => setCommitment({ ...commitment, amount: e.target.value })} required />
                                <span className="donor-field-hint">Minimum {money(minimumCommitment)}. You can enter more.</span>
                            </label>
                            <label className="donor-field">
                                <span className="donor-field-label">Notes <span className="donor-optional">(optional)</span></span>
                                <textarea rows={2} value={commitment.notes} onChange={(e) => setCommitment({ ...commitment, notes: e.target.value })} maxLength={2000} placeholder="e.g. split across two organizations, paid via DAF" />
                            </label>
                        </div>
                        <p className="donor-panel-hint">Your commitment amount is private — only you and the Funds4Charity team can see it.</p>
                        {error && <div className="donor-panel-error" role="alert">{error}</div>}
                        <div className="donor-panel-actions">
                            <button type="button" className="task-action-btn ghost" onClick={() => setOpenPanel(null)}>Cancel</button>
                            <button type="submit" className="task-action-btn" disabled={saving}>{saving ? 'Saving…' : 'Save commitment'}</button>
                        </div>
                    </form>
                )}
            </div>
        </>
    );
};

export default DonorCircleTasks;
